<template>
    <section class="section financeiro-page">
        <div class="level financeiro-header">
            <div>                
                <h1 class="title is-3">Financeiro</h1>                
            </div>
            <div class="financeiro-acoes">
            <b-button
                v-if="isInRole('financeiro-alterar')"
                class="financeiro-acao-novo"
                type="is-primary"
                size="is-medium"
                icon-left="plus"
                rounded
                @click="abrirLancamento"
            >Novo lançamento</b-button>
            </div>
        </div>

        <div class="box financeiro-filtros">
            <div class="columns is-vcentered is-multiline">
                <div class="column is-12-mobile is-3-tablet">
                    <b-field label="Data inicial">
                        <b-input v-model="filtro.dataInicial" type="date" icon="calendar" />
                    </b-field>
                </div>
                <div class="column is-12-mobile is-3-tablet">
                    <b-field label="Data final">
                        <b-input v-model="filtro.dataFinal" type="date" icon="calendar" />
                    </b-field>
                </div>
                <div class="column is-12-mobile is-3-tablet">
                    <b-field label="Tipo">
                        <b-select v-model="filtro.tipo" expanded icon="swap-vertical-bold">
                            <option :value="null">Todos</option>
                            <option :value="1">Receita</option>
                            <option :value="2">Despesa</option>
                        </b-select>
                    </b-field>
                </div>
                <div class="column is-12-mobile is-narrow">
                    <label class="label" style="visibility: hidden;">Data inicial</label>
                    <div class="financeiro-filtro-acoes">
                        <b-button type="is-primary" icon-left="filter" @click="carregar">Filtrar</b-button>
                        <b-dropdown
                            v-if="isInRole('financeiro-alterar')"
                            position="is-bottom-left"
                            aria-role="list"
                        >
                            <template #trigger>
                                <b-button type="is-light" icon-left="dots-horizontal" icon-right="menu-down">
                                    Outras Opções
                                </b-button>
                            </template>
                            <b-dropdown-item
                                v-if="isInRole('financeiro-alterar')"
                                aria-role="listitem"
                                @click="abrirRecorrentes"
                            >
                                <span class="financeiro-opcao-item">
                                    <b-icon icon="calendar-refresh" size="is-small" />
                                    <span>Lançamentos Recorrentes</span>
                                </span>
                            </b-dropdown-item>
                            <b-dropdown-item
                                v-if="isInRole('financeiro-alterar')"
                                aria-role="listitem"
                                :disabled="importandoRecorrentes"
                                @click="importarRecorrentes"
                            >
                                <span class="financeiro-opcao-item">
                                    <b-icon icon="calendar-import" size="is-small" />
                                    <span>Importar Lançamentos Recorrentes</span>
                                </span>
                            </b-dropdown-item>
                        </b-dropdown>
                    </div>
                </div>
            </div>
        </div>

        <div class="box financeiro-grid">
            <div class="financeiro-grid-cabecalho">
                <div>
                    <h2 class="title is-5">Lançamentos</h2>
                    <p>{{ periodoDescricao }}</p>
                </div>
                <b-tag rounded :type="tipoTag">
                    {{ itens.length }} {{ itens.length === 1 ? 'lançamento' : 'lançamentos' }}
                </b-tag>
            </div>

            <b-table
                :data="itens"
                :loading="carregando"
                paginated
                :per-page="15"
                hoverable
                mobile-cards
                empty-message="Nenhum lançamento encontrado no período."
            >
                <b-table-column field="data" label="Data" width="150" v-slot="p">
                    <span class="financeiro-data"><b-icon icon="calendar" size="is-small" /> {{ data(p.row.data) }}</span>
                </b-table-column>
                <b-table-column field="descricao" label="Nome / descrição" v-slot="p">
                    <strong>{{ p.row.descricao }}</strong>
                    <div v-if="p.row.origem === 'Venda'" class="financeiro-nota-fiscal">
                        Emitir Nf?: <strong>{{ p.row.emitirNotaFiscal ? 'Sim' : 'Não' }}</strong>
                    </div>
                </b-table-column>
                <b-table-column field="origem" label="Origem" centered width="140" v-slot="p">
                    <b-tag size="is-small" rounded :type="p.row.origem === 'Venda' ? 'is-info is-light' : 'is-light'">
                        <b-icon :icon="p.row.origem === 'Venda' ? 'cart-outline' : (p.row.origem === 'Recorrente' ? 'calendar-refresh' : 'pencil-outline')" size="is-small" />
                        <span>{{ p.row.origem }}</span>
                    </b-tag>
                </b-table-column>
                <b-table-column field="valor" label="Valor" numeric width="190" v-slot="p">
                    <strong :class="classeValor(p.row.valor)">{{ moeda(p.row.valor) }}</strong>
                </b-table-column>
                <b-table-column label="Ações" centered width="90" v-slot="p">
                    <b-button
                        v-if="p.row.origem !== 'Venda' && isInRole('financeiro-alterar')"
                        type="is-danger"
                        size="is-small"
                        icon-left="close"
                        outlined
                        rounded
                        title="Remover lançamento"
                        aria-label="Remover lançamento"
                        :loading="excluindoId === p.row.lancamentoId"
                        @click="confirmarExclusao(p.row)"
                    />
                </b-table-column>
            </b-table>

            <div class="financeiro-total" :class="classeTotal">
                <span>Total do período</span>
                <strong>{{ moeda(total) }}</strong>
            </div>
        </div>

        <b-modal :active="modal" @update:active="modal = $event" has-modal-card trap-focus>
            <ValidationObserver v-slot="{ handleSubmit }">
                <form class="modal-card" @submit.prevent="handleSubmit(salvar)">
                    <header class="modal-card-head">
                        <div><p class="modal-card-title">Novo lançamento</p><small>Registre uma receita ou despesa avulsa.</small></div>
                    </header>
                    <section class="modal-card-body">
                        <b-field label="Tipo do lançamento">
                            <div class="tipo-lancamento">
                                <b-radio-button v-model="lancamento.tipo" :native-value="1" type="is-success" class="tipo-opcao tipo-receita">
                                    <span class="tipo-icone"><b-icon icon="arrow-up-circle" size="is-medium" /></span>
                                    <span class="tipo-texto"><strong>Receita</strong><small>Entrada de dinheiro</small></span>
                                </b-radio-button>
                                <b-radio-button v-model="lancamento.tipo" :native-value="2" type="is-danger" class="tipo-opcao tipo-despesa">
                                    <span class="tipo-icone"><b-icon icon="arrow-down-circle" size="is-medium" /></span>
                                    <span class="tipo-texto"><strong>Despesa</strong><small>Saída de dinheiro</small></span>
                                </b-radio-button>
                            </div>
                        </b-field>
                        <ValidationProvider rules="required" name="Data" v-slot="{ errors }">
                            <b-field label="Data" :message="errors" :type="errors.length ? 'is-danger' : null"><b-input v-model="lancamento.data" type="date" icon="calendar" /></b-field>
                        </ValidationProvider>
                        <ValidationProvider rules="required|max:300" name="Descrição" v-slot="{ errors }">
                            <b-field label="Nome / descrição" :message="errors" :type="errors.length ? 'is-danger' : null"><b-input v-model="lancamento.descricao" maxlength="300" placeholder="Ex.: Compra de embalagens" /></b-field>
                        </ValidationProvider>
                        <ValidationProvider rules="required|min_value:0.01" name="Valor" v-slot="{ errors }">
                            <b-field label="Valor" :message="errors" :type="errors.length ? 'is-danger' : null"><b-input v-model.number="lancamento.valor" type="number" min="0.01" step="0.01" icon="currency-brl" /></b-field>
                        </ValidationProvider>
                    </section>
                    <footer class="modal-card-foot is-justify-content-flex-end">
                        <b-button @click="modal = false">Cancelar</b-button>
                        <b-button native-type="submit" type="is-primary" :loading="salvando">Salvar lançamento</b-button>
                    </footer>
                </form>
            </ValidationObserver>
        </b-modal>

        <b-modal
            :active="modalRecorrentes"
            @update:active="modalRecorrentes = $event"
            has-modal-card
            trap-focus
            :width="960"
        >
            <ValidationObserver v-slot="{ handleSubmit }" ref="recorrenteObserver">
                <form class="modal-card recorrentes-modal" @submit.prevent="handleSubmit(salvarRecorrente)">
                    <header class="modal-card-head">
                        <div>
                            <p class="modal-card-title">Lançamentos Recorrentes</p>
                            <small>Cadastre receitas e despesas para importar mensalmente.</small>
                        </div>
                    </header>
                    <section class="modal-card-body recorrentes-corpo">
                        <b-loading :is-full-page="false" :active="carregandoRecorrentes" />

                        <div class="recorrentes-formulario">
                            <b-field label="Tipo do lançamento">
                                <div class="tipo-lancamento">
                                    <b-radio-button v-model="recorrente.tipo" :native-value="1" type="is-success" class="tipo-opcao tipo-receita">
                                        <span class="tipo-icone"><b-icon icon="arrow-up-circle" size="is-medium" /></span>
                                        <span class="tipo-texto"><strong>Receita</strong><small>Entrada de dinheiro</small></span>
                                    </b-radio-button>
                                    <b-radio-button v-model="recorrente.tipo" :native-value="2" type="is-danger" class="tipo-opcao tipo-despesa">
                                        <span class="tipo-icone"><b-icon icon="arrow-down-circle" size="is-medium" /></span>
                                        <span class="tipo-texto"><strong>Despesa</strong><small>Sa&iacute;da de dinheiro</small></span>
                                    </b-radio-button>
                                </div>
                            </b-field>
                            <div class="columns is-multiline">
                                <div class="column is-12-mobile is-2-tablet">
                                    <ValidationProvider rules="required|min_value:1|max_value:31" name="Dia" v-slot="{ errors }">
                                        <b-field label="Dia" :message="errors" :type="errors.length ? 'is-danger' : null">
                                            <b-input v-model.number="recorrente.dia" type="number" min="1" max="31" icon="calendar-today" />
                                        </b-field>
                                    </ValidationProvider>
                                </div>
                                <div class="column is-12-mobile is-6-tablet">
                                    <ValidationProvider rules="required|max:300" name="Descrição" v-slot="{ errors }">
                                        <b-field label="Nome / descrição" :message="errors" :type="errors.length ? 'is-danger' : null">
                                            <b-input v-model="recorrente.descricao" maxlength="300" placeholder="Ex.: Aluguel" />
                                        </b-field>
                                    </ValidationProvider>
                                </div>
                                <div class="column is-12-mobile is-4-tablet">
                                    <ValidationProvider rules="required|min_value:0.01" name="Valor" v-slot="{ errors }">
                                        <b-field label="Valor" :message="errors" :type="errors.length ? 'is-danger' : null">
                                            <b-input v-model.number="recorrente.valor" type="number" min="0.01" step="0.01" icon="currency-brl" />
                                        </b-field>
                                    </ValidationProvider>
                                </div>
                            </div>
                            <div class="has-text-right">
                                <b-button native-type="submit" type="is-primary" icon-left="plus" :loading="salvandoRecorrente">
                                    Adicionar ao cadastro
                                </b-button>
                            </div>
                        </div>

                        <b-table
                            :data="recorrentes"
                            :loading="carregandoRecorrentes"
                            hoverable
                            mobile-cards
                            empty-message="Nenhum lançamento recorrente cadastrado."
                        >
                            <b-table-column field="dia" label="Dia" width="90" centered v-slot="p">
                                <b-tag rounded>Dia {{ p.row.dia }}</b-tag>
                            </b-table-column>
                            <b-table-column field="tipo" label="Tipo" width="130" v-slot="p">
                                <b-tag rounded :type="p.row.tipo === 1 ? 'is-success is-light' : 'is-danger is-light'">
                                    {{ p.row.tipo === 1 ? 'Receita' : 'Despesa' }}
                                </b-tag>
                            </b-table-column>
                            <b-table-column field="descricao" label="Nome / descrição" v-slot="p">
                                <strong>{{ p.row.descricao }}</strong>
                            </b-table-column>
                            <b-table-column field="valor" label="Valor" numeric width="170" v-slot="p">
                                <strong :class="p.row.tipo === 1 ? 'has-text-success' : 'has-text-danger'">
                                    {{ moeda(p.row.tipo === 1 ? p.row.valor : -p.row.valor) }}
                                </strong>
                            </b-table-column>
                            <b-table-column label="Ações" centered width="90" v-slot="p">
                                <b-button
                                    type="is-danger"
                                    size="is-small"
                                    icon-left="close"
                                    outlined
                                    rounded
                                    title="Remover lançamento recorrente"
                                    aria-label="Remover lançamento recorrente"
                                    :loading="excluindoRecorrenteId === p.row.id"
                                    @click="confirmarExclusaoRecorrente(p.row)"
                                />
                            </b-table-column>
                        </b-table>
                    </section>
                    <footer class="modal-card-foot is-justify-content-flex-end">
                        <b-button @click="modalRecorrentes = false">Fechar</b-button>
                    </footer>
                </form>
            </ValidationObserver>
        </b-modal>

    </section>
</template>

<script>
import http from '@/http'
import { mapGetters, mapState } from 'vuex'

const dataLocal = data => `${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, '0')}-${String(data.getDate()).padStart(2, '0')}`
const periodoMesAtual = () => {
    const hoje = new Date()
    return {
        dataInicial: dataLocal(new Date(hoje.getFullYear(), hoje.getMonth(), 1)),
        dataFinal: dataLocal(new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0))
    }
}

export default {
    data() {
        return {
            filtro: { ...periodoMesAtual(), tipo: null },
            itens: [],
            total: 0,
            carregando: false,
            salvando: false,
            excluindoId: null,
            modal: false,
            modalRecorrentes: false,
            recorrentes: [],
            carregandoRecorrentes: false,
            salvandoRecorrente: false,
            importandoRecorrentes: false,
            excluindoRecorrenteId: null,
            recorrente: { tipo: 1, dia: 1, descricao: '', valor: null },
            lancamento: { tipo: 1, data: dataLocal(new Date()), descricao: '', valor: null }
        }
    },
    computed: {
        ...mapState(['unidadeId']),
        ...mapGetters(['isInRole']),
        periodoDescricao() {
            if (!this.filtro.dataInicial || !this.filtro.dataFinal) return ''
            return `${this.$moment(this.filtro.dataInicial).format('DD/MM/YYYY')} a ${this.$moment(this.filtro.dataFinal).format('DD/MM/YYYY')}`
        },
        tipoTag() {
            if (this.filtro.tipo === 1) return 'is-success is-light'
            if (this.filtro.tipo === 2) return 'is-danger is-light'
            return 'is-info is-light'
        },
        classeTotal() { return Number(this.total) < 0 ? 'financeiro-total-despesa' : 'financeiro-total-receita' }
    },
    watch: { unidadeId: 'carregar', 'filtro.tipo': 'carregar' },
    created() { this.carregar() },
    methods: {
        carregar() {
            if (!this.unidadeId) return
            if (!this.filtro.dataInicial || !this.filtro.dataFinal || this.filtro.dataFinal < this.filtro.dataInicial) {
                this.erro({ data: { erro: 'Informe um período válido.' } })
                return
            }
            this.carregando = true
            http.get('/api/financeiro', { params: { unidadeId: this.unidadeId, ...this.filtro } })
                .then(r => { this.itens = r.data.itens || []; this.total = Number(r.data.total || 0) })
                .catch(this.erro)
                .finally(() => { this.carregando = false })
        },
        abrirLancamento() {
            this.lancamento = { tipo: this.filtro.tipo || 1, data: dataLocal(new Date()), descricao: '', valor: null }
            this.modal = true
        },
        abrirRecorrentes() {
            this.recorrente = { tipo: 1, dia: 1, descricao: '', valor: null }
            this.modalRecorrentes = true
            this.carregarRecorrentes()
            this.$nextTick(() => this.$refs.recorrenteObserver?.reset())
        },
        carregarRecorrentes() {
            if (!this.unidadeId) return
            this.carregandoRecorrentes = true
            http.get('/api/financeiro/recorrentes', { params: { unidadeId: this.unidadeId } })
                .then(r => { this.recorrentes = (r.data || []).map(item => ({ ...item, valor: Number(item.valor || 0) })) })
                .catch(this.erro)
                .finally(() => { this.carregandoRecorrentes = false })
        },
        salvarRecorrente() {
            this.salvandoRecorrente = true
            http.post('/api/financeiro/recorrentes', this.recorrente, { params: { unidadeId: this.unidadeId } })
                .then(r => {
                    this.recorrentes.push({ ...r.data, valor: Number(r.data.valor || 0) })
                    this.recorrentes.sort((a, b) => a.dia - b.dia || a.descricao.localeCompare(b.descricao))
                    this.recorrente = { tipo: 1, dia: 1, descricao: '', valor: null }
                    this.$nextTick(() => this.$refs.recorrenteObserver?.reset())
                    this.$buefy.toast.open({ message: 'Lan\u00e7amento recorrente cadastrado.', type: 'is-success' })
                })
                .catch(this.erro)
                .finally(() => { this.salvandoRecorrente = false })
        },
        confirmarExclusaoRecorrente(item) {
            this.$buefy.dialog.confirm({
                title: 'Remover lan\u00e7amento recorrente',
                message: 'Deseja remover este item do cadastro recorrente? Lan\u00e7amentos j\u00e1 importados n\u00e3o ser\u00e3o removidos.',
                confirmText: 'Remover',
                cancelText: 'Cancelar',
                type: 'is-danger',
                hasIcon: true,
                onConfirm: () => this.excluirRecorrente(item)
            })
        },
        excluirRecorrente(item) {
            this.excluindoRecorrenteId = item.id
            http.delete(`/api/financeiro/recorrentes/${item.id}`, { params: { unidadeId: this.unidadeId } })
                .then(() => {
                    this.recorrentes = this.recorrentes.filter(x => x.id !== item.id)
                    this.$buefy.toast.open({ message: 'Lan\u00e7amento recorrente removido.', type: 'is-success' })
                })
                .catch(this.erro)
                .finally(() => { this.excluindoRecorrenteId = null })
        },
        importarRecorrentes() {
            if (!this.periodoValido()) return
            const inicio = this.$moment(this.filtro.dataInicial)
            const fim = this.$moment(this.filtro.dataFinal)
            if (inicio.format('YYYY-MM') !== fim.format('YYYY-MM')) {
                this.erro({ data: { erro: 'Selecione datas dentro do mesmo m\u00eas para importar os lan\u00e7amentos recorrentes.' } })
                return
            }
            this.$buefy.dialog.confirm({
                title: 'Importar lan\u00e7amentos recorrentes',
                message: `Deseja inserir os lan\u00e7amentos recorrentes em <strong>${inicio.format('MM/YYYY')}</strong>? Itens j\u00e1 importados neste m\u00eas ser\u00e3o ignorados.`,
                confirmText: 'Sim, importar',
                cancelText: 'Cancelar',
                type: 'is-primary',
                hasIcon: true,
                onConfirm: this.executarImportacaoRecorrentes
            })
        },
        executarImportacaoRecorrentes() {
            this.importandoRecorrentes = true
            http.post('/api/financeiro/recorrentes/importar', {
                dataInicial: this.filtro.dataInicial,
                dataFinal: this.filtro.dataFinal
            }, { params: { unidadeId: this.unidadeId } })
                .then(r => {
                    const importados = Number(r.data.importados || 0)
                    const ignorados = Number(r.data.ignorados || 0)
                    const mensagem = importados
                        ? `${importados} lan\u00e7amento(s) recorrente(s) importado(s).${ignorados ? ` ${ignorados} j\u00e1 existente(s) foi(ram) ignorado(s).` : ''}`
                        : 'Nenhum novo lan\u00e7amento recorrente para importar neste m\u00eas.'
                    this.$buefy.toast.open({ message: mensagem, type: importados ? 'is-success' : 'is-info', duration: 5000 })
                    this.carregar()
                })
                .catch(this.erro)
                .finally(() => { this.importandoRecorrentes = false })
        },
        periodoValido() {
            if (!this.unidadeId || !this.filtro.dataInicial || !this.filtro.dataFinal || this.filtro.dataFinal < this.filtro.dataInicial) {
                this.erro({ data: { erro: 'Informe um per\u00edodo v\u00e1lido.' } })
                return false
            }
            return true
        },
        salvar() {
            this.salvando = true
            http.post('/api/financeiro', this.lancamento, { params: { unidadeId: this.unidadeId } })
                .then(() => {
                    this.modal = false
                    if (this.filtro.tipo == null) {
                        this.carregar()
                    } else {
                        const mudouTipo = this.filtro.tipo !== this.lancamento.tipo
                        this.filtro.tipo = this.lancamento.tipo
                        if (!mudouTipo) this.carregar()
                    }
                    this.$buefy.toast.open({ message: 'Lançamento salvo com sucesso.', type: 'is-success' })
                })
                .catch(this.erro)
                .finally(() => { this.salvando = false })
        },
        confirmarExclusao(item) {
            const recorrente = item.origem === 'Recorrente'
            this.$buefy.dialog.confirm({
                title: 'Remover lançamento',
                message: recorrente
                    ? 'Deseja remover este lançamento importado? O cadastro recorrente será mantido.'
                    : 'Deseja remover este lançamento manual? Esta ação não poderá ser desfeita.',
                confirmText: 'Remover',
                cancelText: 'Cancelar',
                type: 'is-danger',
                hasIcon: true,
                onConfirm: () => this.excluir(item)
            })
        },
        excluir(item) {
            this.excluindoId = item.lancamentoId
            http.delete(`/api/financeiro/${item.lancamentoId}`, { params: { unidadeId: this.unidadeId } })
                .then(() => {
                    this.$buefy.toast.open({ message: 'Lançamento removido com sucesso.', type: 'is-success' })
                    this.carregar()
                })
                .catch(this.erro)
                .finally(() => { this.excluindoId = null })
        },
        erro(e) { this.$buefy.toast.open({ message: e.data?.erro || 'Não foi possível concluir a operação.', type: 'is-danger' }) },
        data(valor) { return this.$moment(valor).format('DD/MM/YYYY') },
        moeda(valor) { return Number(valor || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) },
        classeValor(valor) { return Number(valor) < 0 ? 'has-text-danger' : 'has-text-success' }
    }
}
</script>

<style scoped>
.financeiro-page { padding: .75rem !important; background: linear-gradient(180deg, #f7faf8 0, #fff 260px); min-height: 100%; }
.financeiro-header { align-items: flex-end; margin-bottom: 1.5rem; }
.financeiro-header .title { margin: .15rem 0 .35rem; color: #20362d; }
.financeiro-header .subtitle { margin: 0; color: #718078; }
.financeiro-acoes { display: flex; align-items: center; }
.financeiro-acoes .button { min-width: 190px; margin: 0; font-weight: 700; transition: transform .15s ease, box-shadow .15s ease; }
.financeiro-acoes .button:hover { transform: translateY(-1px); }
.financeiro-acao-novo { box-shadow: 0 5px 14px rgba(72,199,142,.22); }
.financeiro-filtro-acoes { display: flex; align-items: center; gap: .5rem; }
.financeiro-filtro-acoes .button { white-space: nowrap; }
.financeiro-opcao-item { display: inline-flex; align-items: center; gap: .5rem; white-space: nowrap; }
.recorrentes-modal { width: 960px; max-width: calc(100vw - 40px); }
.recorrentes-corpo { position: relative; }
.recorrentes-formulario { margin-bottom: 1.25rem; padding: 1rem; background: #f8fbf9; border: 1px solid #e3ebe7; border-radius: 12px; }
.recorrentes-formulario .columns { margin-top: .15rem; margin-bottom: 0; }
.financeiro-eyebrow { color: #2f855a; font-size: .72rem; font-weight: 800; letter-spacing: .14em; }
.financeiro-filtros { border: 1px solid #e4ebe7; border-radius: 14px; box-shadow: 0 8px 28px rgba(32,54,45,.06); }
.financeiro-grid { overflow: hidden; padding: 0; border: 1px solid #e4ebe7; border-radius: 14px; box-shadow: 0 8px 28px rgba(32,54,45,.06); }
.financeiro-grid-cabecalho { display: flex; align-items: center; justify-content: space-between; padding: 1.25rem 1.4rem; border-bottom: 1px solid #edf1ef; }
.financeiro-grid-cabecalho .title { margin-bottom: .2rem; color: #263b32; }
.financeiro-grid-cabecalho p { color: #819087; font-size: .84rem; }
.financeiro-grid ::v-deep .table-wrapper { margin-bottom: 0; }
.financeiro-grid ::v-deep table { margin-bottom: 0; }
.financeiro-grid ::v-deep th { color: #6b7972; background: #fafcfb; font-size: .74rem; letter-spacing: .04em; text-transform: uppercase; }
.financeiro-data { display: inline-flex; align-items: center; gap: .35rem; color: #617169; white-space: nowrap; }
.financeiro-nota-fiscal { margin-top: .2rem; color: #617169; font-size: .82rem; }
.financeiro-total { display: flex; align-items: center; justify-content: flex-end; gap: 2rem; padding: 1.2rem 1.5rem; border-top: 1px solid #e8eeeb; font-size: 1rem; }
.financeiro-total strong { min-width: 180px; font-size: 1.55rem; text-align: right; }
.financeiro-total-receita { color: #237a4b; background: #f0faf4; }
.financeiro-total-despesa { color: #c0392b; background: #fff5f4; }
.tipo-lancamento { display: grid; grid-template-columns: 1fr 1fr; gap: .8rem; width: 100%; }
.tipo-opcao { width: 100%; }
.tipo-opcao ::v-deep .button { display: flex; align-items: center; justify-content: flex-start; width: 100%; height: 76px; padding: .8rem 1rem; color: #4a5a52; background: #fff; border: 2px solid #e4ebe7; border-radius: 12px !important; box-shadow: none; transition: border-color .15s ease, background .15s ease, transform .15s ease, box-shadow .15s ease; }
.tipo-opcao ::v-deep .button:hover { border-color: #b9c9c0; transform: translateY(-1px); box-shadow: 0 6px 16px rgba(32,54,45,.08); }
.tipo-receita ::v-deep .button.is-selected { color: #1f7045; background: #edf9f2; border-color: #48c78e; box-shadow: 0 0 0 3px rgba(72,199,142,.12); }
.tipo-despesa ::v-deep .button.is-selected { color: #b42318; background: #fff1f0; border-color: #f14668; box-shadow: 0 0 0 3px rgba(241,70,104,.1); }
.tipo-icone { display: inline-flex; margin-right: .7rem; }
.tipo-texto { display: flex; flex-direction: column; align-items: flex-start; line-height: 1.2; }
.tipo-texto strong { font-size: 1rem; }
.tipo-texto small { margin-top: .2rem; color: #7b8881; font-size: .73rem; }
@media (max-width: 768px) {
    .financeiro-page { padding: .5rem !important; }
    .financeiro-header { display: block; }
    .financeiro-acoes { display: block; margin-top: 1rem; }
    .financeiro-acoes .button { width: 100%; min-width: 0; }
    .financeiro-filtro-acoes { width: 100%; }
    .financeiro-filtro-acoes > .button { flex: 1; }
    .financeiro-grid-cabecalho { align-items: flex-start; gap: .75rem; }
    .financeiro-total { justify-content: space-between; gap: .75rem; padding: 1rem; }
    .financeiro-total strong { min-width: 0; font-size: 1.25rem; }
    .tipo-lancamento { grid-template-columns: 1fr; gap: .6rem; }
    .tipo-opcao ::v-deep .button { height: 68px; }
}
</style>
